import { motion } from 'framer-motion';
import MainHeading from '@/components/heading/MainHeading';
import { Product } from '@/core/global-data/products.data';
import { PrimaryButton } from '@/components/buttons/PrimaryButton';
import { useState } from 'react';
import ProductForm from '@/features/complaints/components/form/ProductForm';
import Products from '@/features/homePage/components/products/Products';
import ProductAdvantages from './ProductAdvantages';
import { useTranslation } from 'react-i18next';
import SliderImgs from '../../slider/SliderImgs';
import { ProductFeatureSection, type ProductFeature } from './ProductFeatureSection';

interface Props {
    product: Product;
}

export default function ProductDetails({ product }: Props) {
    const { t } = useTranslation();
    const [openModal, setOpenModal] = useState(false);
    const featuresData = t(`products.${product.id}.features`, {
        returnObjects: true,
        defaultValue: [],
    });
    const features = Array.isArray(featuresData) ? (featuresData as ProductFeature[]) : [];

    const fadeInUp = {
        initial: { opacity: 0, y: 50 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, amount: 0.2 },
        transition: { duration: 0.6 },
    };

    return (
        <div className="mt-[86px] ">
            <motion.div {...fadeInUp} className="flex flex-row items-center justify-start p-2">
                <MainHeading>{t(`products.${product.id}.title`)}</MainHeading>
            </motion.div>

            <motion.div {...fadeInUp} className="mt-6">
                {Array.isArray(product.img) ? <SliderImgs data={product.img} /> : null}
            </motion.div>

            <div className="md:mt-[70px] mt-[50px]">
                <motion.div {...fadeInUp}>
                    <h3 className="text-[#145DA0] dark:text-[#86A4C0] font-cairo text-xl lg:text-[24px] leading-[100%] lg:leading-[28px] font-semibold text-start">
                        {t('products.descriptionLabel')}
                    </h3>
                    <ul className="mt-6">
                        <li className="list-disc text-product-dec ms-8 font-medium text-sm lg:text-[20px] leading-[32px] text-start">
                            {t(`products.${product.id}.description`)}
                        </li>
                    </ul>
                </motion.div>
            </div>

            <ProductAdvantages advantages={product.advantages} />
            {features.length > 0 ? (
                <div className="flex flex-col gap-10">
                    {features.map((feature, index) => (
                        <ProductFeatureSection
                            key={`${feature.title}-${index}`}
                            index={index}
                            reverse={index % 2 === 1}
                            icon={feature.icon}
                            title={feature.title}
                            description={feature.description}
                        />
                    ))}
                </div>
            ) : null}

            <div className="md:mt-[70px] mt-[50px]">
                <Products />
            </div>

            <div className="md:mt-[70px] mt-[50px]">
                <p className="text-primery-blue-900 lg:text-[24px] md:text-[20px] sm:text-[16px] text-[14px] mt-[40px] text-center mb-[24px] md:px-0 px-3">
                    {t('products.ctaHint')}
                </p>
                <button onClick={() => setOpenModal(true)} className="mx-auto flex items-center justify-center">
                    <PrimaryButton className="bg-primary-button text-[#ffff] hover:bg-[#1e63a3] tracking-normal font-cairo px-[45px] py-4 md:px-[65px] lg:py-[16px] md:py-[12px] lg:px-[85px] text-sm md:text-base font-bold rounded-lg leading-[125%]">
                        {t('products.ctaButton')}
                    </PrimaryButton>
                </button>
            </div>
            {openModal && <ProductForm isOpen={openModal} onClose={() => setOpenModal(false)} />}
        </div>
    );
}
